// Pure view layer for Zone Finder ("Action Map" in the UI) — ported from
// js/renderers-zonefinder.js (read its file-top comment: the module is EXPERIMENTAL and
// ring-fenced — its own data file, its own season, nothing else depends on it).
//
// What lives here: the 22 action types (2 shot/pass + 20 StatsBomb open-data), their
// "How" filter defs, the two populations, the match-info lookup behind "vs Opponent",
// the dot tooltip / radius / hit-test, the ranked leaderboard, and the fixed texts.
// ZoneFinderModule (04m-b) renders it.
//
// Deviations from the legacy, all behaviour-neutral:
// - Data is passed IN (the loaded file + the players), never read off window — the legacy's
//   loader wrote the file onto window.CatalystData (Ben's MEDIUM risk); ours never does.
// - Zoom, "event inside the zone" and the drag → zone rectangle are the kit's
//   pitchZoomView / eventInArea / areaFromDrag: the legacy's local copies
//   (applyZoneFinderZoom, eventInZone, wireZoneDraw's clamp + ZONE_MIN_SIZE 3 +
//   ZONE_DRAG_THRESHOLD_PX 6) are the same maths, so they aren't duplicated.
// - A dot's tooltip is built when it's hovered, not for every dot on every render
//   (Carries has ~10k dots). Same text.

import { playerById, type PlayerId } from "../data/players.js";
import type { ProtoEvent, ProtoZoneFinderData, ProtoZoneMatch, ProtoZoneSbEvent } from "@catbento/shared";
import type { RawPlayerRecord } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import {
  PITCH_W,
  eventInArea,
  humanizeSeasonCode,
  matchesEventFilters,
  passFilters,
  shotFilters,
  type EventArea,
  type EventActive,
  type EventContext,
  type EventFilterDef,
} from "../engines/event-map-kit.js";
import {
  FILTER_LABELS,
  formatCategoricalValue,
  formatRangeReadout,
  isRangeFilter,
  matchesFilters,
  type ActiveFilters,
  type CategoricalActive,
  type KitPlayer,
  type RangeActive,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";
import { ageFromDob } from "./squad-view.js";
import { clockMinute } from "../data/match-minute.js";

// --- Fixed scope -------------------------------------------------------------------
// The whole module is pinned to the one season its StatsBomb capture covers, so the
// league + year "pickers" are single fixed options (they only mirror Heat Map's strip).

export const ZONE_SEASON_LABEL = "FAWSL 2023/24";
/** The build script's ZF_TEST_SEASON_CODE; the label comes from humanizeSeasonCode so the
 *  two can't drift in spelling. */
export const ZONE_YEAR_CODE = "52023";
/** Competition 37, named exactly as Heat Map's competition select names it. */
export const ZONE_LEAGUE_OPTIONS = [{ value: "37", label: "FAWSL (FA Women's Super League)" }] as const;

export function zoneYearOptions(): { value: string; label: string }[] {
  return [{ value: ZONE_YEAR_CODE, label: humanizeSeasonCode(ZONE_YEAR_CODE) }];
}

// --- "Who" range filters -------------------------------------------------------------
// Absolute ranges (a database-wide query has no reference player to be relative to). Same
// domains as Rankings'; the legacy keeps its own copy per file, and so do we.
export const ZONE_RANGE_FILTERS: RangeFilterConfig = {
  age: {
    label: "Age range",
    chipLabel: "Age",
    unit: "y",
    relative: false,
    domain: { min: 15, max: 42 },
    getValue: (p) => ageFromDob(p.dob ?? null),
  },
  height: {
    label: "Height range",
    chipLabel: "Height",
    unit: "cm",
    relative: false,
    domain: { min: 151, max: 185 },
    getValue: (p) => (typeof p.height === "number" ? p.height : null),
  },
  weight: {
    label: "Weight range",
    chipLabel: "Weight",
    unit: "kg",
    relative: false,
    domain: { min: 48, max: 81 },
    getValue: (p) => (typeof p.weight === "number" ? p.weight : null),
  },
};

// --- "How" filters for the StatsBomb action types ---------------------------------------
// Types with no real sub-flag variance (checked against the data upstream) get Half only.

type SbFilterDef = EventFilterDef<ProtoZoneSbEvent>;
type SbFlag = "ledToShot" | "underPressure" | "counterpress" | "recoveryFailure" | "out";

export const SB_HALF_FILTER: SbFilterDef = {
  key: "half",
  label: "Half",
  getValue: (e) => (e.period === 1 ? "1st Half" : e.period === 2 ? "2nd Half" : `Period ${e.period}`),
};
export const SB_OUTCOME_FILTER: SbFilterDef = { key: "outcome", label: "Outcome", getValue: (e) => e.outcome || undefined };
export const SB_YESNO = (key: SbFlag, label: string): SbFilterDef => ({ key, label, getValue: (e) => (e[key] ? "Yes" : "No") });

export const SB_FILTER_DEFS: Record<ZoneSbActionKey, SbFilterDef[]> = {
  tackles: [SB_OUTCOME_FILTER, SB_HALF_FILTER],
  blocks: [SB_HALF_FILTER],
  foulsWon: [SB_HALF_FILTER],
  foulsCommitted: [SB_HALF_FILTER],
  dribbles: [SB_OUTCOME_FILTER, SB_YESNO("ledToShot", "Led to Shot"), SB_HALF_FILTER],
  interceptions: [SB_OUTCOME_FILTER, SB_HALF_FILTER],
  clearances: [{ key: "bodyPart", label: "Body Part", getValue: (e) => e.bodyPart || undefined }, SB_HALF_FILTER],
  aerials: [SB_HALF_FILTER],
  carries: [SB_YESNO("underPressure", "Under Pressure"), SB_HALF_FILTER],
  ballReceipts: [SB_OUTCOME_FILTER, SB_HALF_FILTER],
  pressures: [SB_YESNO("counterpress", "Counterpress"), SB_HALF_FILTER],
  ballRecoveries: [SB_YESNO("recoveryFailure", "Recovery Failure"), SB_HALF_FILTER],
  dispossessed: [SB_HALF_FILTER],
  miscontrols: [SB_YESNO("out", "Went Out of Play"), SB_HALF_FILTER],
  dribbledPast: [SB_YESNO("counterpress", "Counterpress"), SB_HALF_FILTER],
  fiftyFifties: [SB_OUTCOME_FILTER, SB_HALF_FILTER],
  shields: [SB_HALF_FILTER],
  goalkeeperActions: [{ key: "gkType", label: "Type", getValue: (e) => e.gkType || undefined }, SB_HALF_FILTER],
  errors: [SB_HALF_FILTER],
  offsides: [SB_HALF_FILTER],
};

// --- Action types ---------------------------------------------------------------------

export type ZoneSbActionKey =
  | "blocks"
  | "tackles"
  | "foulsWon"
  | "foulsCommitted"
  | "dribbles"
  | "interceptions"
  | "clearances"
  | "aerials"
  | "carries"
  | "ballReceipts"
  | "pressures"
  | "ballRecoveries"
  | "dispossessed"
  | "miscontrols"
  | "dribbledPast"
  | "fiftyFifties"
  | "shields"
  | "goalkeeperActions"
  | "errors"
  | "offsides";
export type ZoneActionKey = "passes" | "shots" | ZoneSbActionKey;

/** An event of either capture. Each action type only ever sees its own family's events. */
export type ZoneEvent = ProtoEvent | ProtoZoneSbEvent;
export type ZoneFilterDef = EventFilterDef<ZoneEvent>;

export interface ZoneActionType {
  key: ZoneActionKey;
  label: string;
  noun: string;
  /** Which capture the population reads: StatsBomb open data, or the shots/passes copy. */
  source: "statsBomb" | "shotsPasses";
  filterDefs: ZoneFilterDef[];
}

// Picker order = this order (the legacy's object key order).
const ZONE_ACTION_META: readonly (readonly [ZoneActionKey, string, string])[] = [
  ["passes", "Passes", "passes"],
  ["shots", "Shots", "shots"],
  ["blocks", "Blocks", "blocks"],
  ["tackles", "Tackles", "tackles"],
  ["foulsWon", "Fouls Won", "fouls won"],
  ["foulsCommitted", "Fouls Committed", "fouls committed"],
  ["dribbles", "Dribbles", "dribbles"],
  ["interceptions", "Interceptions", "interceptions"],
  ["clearances", "Clearances", "clearances"],
  ["aerials", "Aerials (Lost)", "lost aerial duels"],
  ["carries", "Carries", "carries"],
  ["ballReceipts", "Ball Receipts", "ball receipts"],
  ["pressures", "Pressures", "pressures"],
  ["ballRecoveries", "Ball Recoveries", "ball recoveries"],
  ["dispossessed", "Dispossessed", "times dispossessed"],
  ["miscontrols", "Miscontrols", "miscontrols"],
  ["dribbledPast", "Dribbled Past", "times dribbled past"],
  ["fiftyFifties", "50/50s", "50/50 contests"],
  ["shields", "Shields", "shields"],
  ["goalkeeperActions", "Goalkeeper Actions", "goalkeeper actions"],
  ["errors", "Errors", "errors"],
  ["offsides", "Offsides", "offsides"],
];

/** The default action type (the legacy seeds `_zoneEventType = 'passes'`). */
export const ZONE_DEFAULT_ACTION: ZoneActionKey = "passes";

/**
 * All 22 action types, in picker order. Passes/Shots reuse Pass/Shot Map's filters minus
 * Opposition + Game State: those resolve through matchResults, which only covers the
 * CURRENT season, and this module is pinned to 2023/24 — so they'd never match anything.
 */
export function zoneActionTypes(ctx: EventContext): Record<ZoneActionKey, ZoneActionType> {
  const dropUnresolvable = (defs: EventFilterDef[]) => defs.filter((f) => f.key !== "opposition" && f.key !== "gameState");
  // The cast: one record holds both families' defs. Safe because an action type's defs
  // only ever read the events its own population returns (same key picks both).
  const passDefs = dropUnresolvable(passFilters(ctx)) as unknown as ZoneFilterDef[];
  const shotDefs = dropUnresolvable(shotFilters(ctx)) as unknown as ZoneFilterDef[];
  const out = {} as Record<ZoneActionKey, ZoneActionType>;
  for (const [key, label, noun] of ZONE_ACTION_META) {
    const isShotPass = key === "passes" || key === "shots";
    out[key] = {
      key,
      label,
      noun,
      source: isShotPass ? "shotsPasses" : "statsBomb",
      filterDefs: key === "passes" ? passDefs : key === "shots" ? shotDefs : (SB_FILTER_DEFS[key] as unknown as ZoneFilterDef[]),
    };
  }
  return out;
}

/** `_zoneEventType`, or the default when it's unset / not a known type. */
export function resolveZoneActionKey(key: string | null | undefined): ZoneActionKey {
  return key && ZONE_ACTION_META.some(([k]) => k === key) ? (key as ZoneActionKey) : ZONE_DEFAULT_ACTION;
}

/** The "What" picker's options (labels, in order) and the reverse lookup. */
export function zoneWhatOptions(): string[] {
  return ZONE_ACTION_META.map(([, label]) => label);
}
export function zoneActionKeyForLabel(label: string): ZoneActionKey | undefined {
  return ZONE_ACTION_META.find(([, l]) => l === label)?.[0];
}

// --- Populations ------------------------------------------------------------------------

export type ZonePlayer = RawPlayerRecord & { id?: unknown };

/** Players by String(id) — how both captures key their players. Last one wins on a clash,
 *  as the legacy's Map.set loop. */
export function zonePlayersById(players: Record<string, ZonePlayer>): Map<string, ZonePlayer> {
  const byId = new Map<string, ZonePlayer>();
  for (const p of Object.values(players)) byId.set(String(p.id), p);
  return byId;
}

export interface ZoneRow {
  player: ZonePlayer;
  events: ZoneEvent[];
}

/** Every captured player with at least one event of this type. A capture keyed by an id
 *  that isn't in the roster is skipped. */
export function zonePopulation(data: ProtoZoneFinderData, byId: Map<string, ZonePlayer>, type: ZoneActionType): ZoneRow[] {
  const rows: ZoneRow[] = [];
  if (type.source === "statsBomb") {
    for (const pid of Object.keys(data.statsBombOpenData)) {
      const player = byId.get(pid);
      if (!player) continue;
      const events = (data.statsBombOpenData[pid]!.events || []).filter((e) => e.actionType === type.key);
      if (events.length) rows.push({ player, events });
    }
  } else {
    const field = type.key as "passes" | "shots";
    for (const pid of Object.keys(data.shotsPasses)) {
      const player = byId.get(pid);
      if (!player) continue;
      const events = ((data.shotsPasses[pid] && data.shotsPasses[pid]![field]) || []) as ZoneEvent[];
      if (events.length) rows.push({ player, events });
    }
  }
  return rows;
}

export interface ZoneEntry {
  event: ZoneEvent;
  x: number;
  y: number;
  player: ZonePlayer;
}

/** One entry per event, credited to the player whose capture it came from. */
export function zoneEntries(rows: ZoneRow[]): ZoneEntry[] {
  return rows.flatMap((row) => row.events.map((e) => ({ event: e, x: e.x, y: e.y, player: row.player })));
}

// --- Opposition --------------------------------------------------------------------------

/** matchId → the match, merged across every player's `matches` (each only covers the
 *  games that player appeared in). The shots/passes events share the same id space. */
export function buildZoneMatchInfo(data: ProtoZoneFinderData): Map<number, ProtoZoneMatch> {
  const info = new Map<number, ProtoZoneMatch>();
  for (const entry of Object.values(data.statsBombOpenData)) {
    for (const m of Object.values(entry.matches || {})) info.set(m.matchId, m);
  }
  return info;
}

/** The other side in this event's match, or null when it can't be told (no match found,
 *  or the player's team is neither side) — never a made-up "Unknown". StatsBomb events
 *  spell the key `matchId`, the shots/passes ones `match_id`. */
export function zoneOpposition(
  info: Map<number, ProtoZoneMatch>,
  event: ZoneEvent,
  playerTeam: string | null | undefined,
): string | null {
  const camel = (event as { matchId?: number }).matchId;
  const matchId = camel != null ? camel : (event as { match_id?: number }).match_id;
  const m = matchId == null ? undefined : info.get(matchId);
  if (!m || !playerTeam) return null;
  if (m.homeTeam === playerTeam) return m.awayTeam;
  if (m.awayTeam === playerTeam) return m.homeTeam;
  return null;
}

/** "Name — Type" / "vs Opp" (only when known) / up to 3 filter values + the minute. */
export function zoneDotTooltip(type: ZoneActionType, entry: ZoneEntry, info: Map<number, ProtoZoneMatch>): string {
  const name = getPlayerShortName(entry.player) || entry.player.name || "";
  const minuteNum = Number((entry.event as { minute?: unknown }).minute);
  const minute = Number.isFinite(minuteNum) ? clockMinute(minuteNum) : "";
  const details = type.filterDefs
    .map((f) => f.getValue(entry.event))
    .filter((v) => v !== undefined && v !== null && v !== "")
    .slice(0, 3);
  const line2 = [...details, minute].filter(Boolean).join(" · ");
  const opposition = zoneOpposition(info, entry.event, entry.player.team);
  const lines = [`${name} — ${type.label}`];
  if (opposition) lines.push(`vs ${opposition}`);
  if (line2) lines.push(line2);
  return lines.join("\n");
}

// --- Dots ------------------------------------------------------------------------------

export const ZONE_DOT_BASE_RADIUS = 0.45;
/** 0 = dots keep one on-screen size at any zoom, 1 = they grow with the zoom in full.
 *  0.5 = a 4× zoom shows them 2× bigger. */
export const ZONE_DOT_GROWTH = 0.5;

/** Dot radius in pitch units for the current zoom (the zoom is the zone itself). */
export function zoneDotRadius(zone: EventArea | null): number {
  const postW = zone ? zone.y2 - zone.y1 : PITCH_W;
  const zoomFactor = postW / PITCH_W;
  return ZONE_DOT_BASE_RADIUS * Math.pow(zoomFactor, 1 - ZONE_DOT_GROWTH);
}

export interface ZoneDots {
  /** The entries drawn, in order (one <circle> each). */
  dots: ZoneEntry[];
  radius: number;
  /** Nearest-dot hover reach — 3× the drawn radius, so it scales with the zoom too. */
  hitRadius: number;
}

/** The dots to draw: every plottable entry with a numeric x/y inside the zone. */
export function zoneDots(entries: ZoneEntry[], zone: EventArea | null): ZoneDots {
  const radius = zoneDotRadius(zone);
  const dots = entries.filter((en) => typeof en.x === "number" && typeof en.y === "number" && eventInArea(en, zone));
  return { dots, radius, hitRadius: radius * 3 };
}

/** The dot nearest a pitch-space point, if within reach (first-nearest wins a tie). */
export function nearestZoneDot(dots: ZoneEntry[], p: { x: number; y: number }, hitRadius: number): ZoneEntry | null {
  let best: ZoneEntry | null = null;
  let bestDist = Infinity;
  for (const d of dots) {
    const dist = Math.hypot(p.x - d.x, p.y - d.y);
    if (dist < bestDist) {
      bestDist = dist;
      best = d;
    }
  }
  return best && bestDist <= (hitRadius || 1) ? best : null;
}

// --- Leaderboard -----------------------------------------------------------------------

/** The sticky player (else the first player) — only so "Who" chips have a player to seed
 *  their first value from. The ranges are absolute, so it never narrows anything itself. */
export function zoneRefPlayer(players: Record<string, ZonePlayer>, sticky: PlayerId | null): ZonePlayer | null {
  const first = Object.keys(players)[0];
  return playerById(players, sticky) || (first !== undefined ? players[first] : undefined) || null;
}

/** Case-insensitive substring on the short name; a blank search matches everyone. */
export function matchesZoneNameSearch(player: ZonePlayer, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return getPlayerShortName(player).toLowerCase().includes(q);
}

export interface ZoneQuery {
  filterDefs: ZoneFilterDef[];
  eventActive: EventActive;
  bioActive: ActiveFilters;
  refPlayer: ZonePlayer | null;
  nameSearch: string;
}

const NO_REF: KitPlayer = {};

function matchesZoneQuery(en: ZoneEntry, q: ZoneQuery): boolean {
  return (
    matchesEventFilters(en.event, q.eventActive, q.filterDefs) &&
    matchesFilters(en.player, q.bioActive, q.refPlayer ?? NO_REF, ZONE_RANGE_FILTERS) &&
    matchesZoneNameSearch(en.player, q.nameSearch)
  );
}

/** The entries to plot: Who + How + name search — NOT the zone (zoneDots applies it). */
export function zonePlottable(entries: ZoneEntry[], q: ZoneQuery): ZoneEntry[] {
  return entries.filter((en) => matchesZoneQuery(en, q));
}

export interface ZoneRankedRow {
  player: ZonePlayer;
  count: number;
  value: number;
}

/** Entries inside the zone that pass every filter, counted per player (by name), zeros
 *  dropped, highest first (a stable sort, so ties keep first-seen order). */
export function zoneLeaderboard(entries: ZoneEntry[], zone: EventArea | null, q: ZoneQuery): ZoneRankedRow[] {
  const grouped = new Map<string | undefined, { player: ZonePlayer; count: number }>();
  for (const en of entries) {
    if (!eventInArea(en, zone)) continue;
    if (!matchesZoneQuery(en, q)) continue;
    const key = en.player.name;
    let g = grouped.get(key);
    if (!g) {
      g = { player: en.player, count: 0 };
      grouped.set(key, g);
    }
    g.count++;
  }
  return Array.from(grouped.values())
    .map(({ player, count }) => ({ player, count, value: count }))
    .filter((r) => r.value !== 0)
    .sort((a, b) => b.value - a.value);
}

/** A leaderboard row's second line: team · competition (whichever exist). */
export function zoneRowMeta(p: ZonePlayer): string {
  return [p.team, p.competition].filter(Boolean).join(" · ");
}

export function formatCount(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

// --- Texts -------------------------------------------------------------------------------

/** The empty leaderboard's message: name search first, then the Who chips, then generic. */
export function zoneEmptyText(nameSearch: string, bioActive: ActiveFilters, noun: string): string {
  const search = nameSearch.trim();
  if (search) return `No player matching "${search}" recorded any real ${noun} in the selected zone, in ${ZONE_SEASON_LABEL}.`;
  const bioKeys = Object.keys(bioActive);
  if (bioKeys.length) {
    const labels = bioKeys.map((k) =>
      isRangeFilter(k, ZONE_RANGE_FILTERS)
        ? `${ZONE_RANGE_FILTERS[k]!.chipLabel}: ${formatRangeReadout(k, bioActive[k] as RangeActive, ZONE_RANGE_FILTERS)}`
        : `${FILTER_LABELS[k]}: ${formatCategoricalValue(bioActive[k] as CategoricalActive)}`,
    );
    return `No players match all of: ${labels.join(", ")}.`;
  }
  return `No real ${noun} matching the current filters were recorded in the selected zone, in ${ZONE_SEASON_LABEL}.`;
}

/** The module's own `_zoneLoadState` (unset = idle). 'loaded' only matters once the data
 *  is actually there; if it isn't (e.g. a restored state), the prompt shows as idle. */
export type ZoneLoadState = "loading" | "loaded" | "error" | undefined;

/** Shown in red on every load-prompt state — true whatever the loading state. */
export const ZONE_EXPERIMENTAL_TEXT = "This is an experimental module";

/** The load prompt's text + button label (null = no button while loading). */
export function zoneLoadPrompt(state: ZoneLoadState): { text: string; button: string | null } {
  if (state === "loading") return { text: "Loading real event data…", button: null };
  if (state === "error") {
    return { text: "Could not load the event-data file — check your connection and try again.", button: "Retry" };
  }
  return {
    text:
      "Real event data for every action type on this module comes from a separate file (not loaded by default, to " +
      `keep the page light for everyone else). Sourced from StatsBomb's open data, ${ZONE_SEASON_LABEL} — Arsenal WFC's ` +
      "own squad only, not the full league.",
    button: "Load event data",
  };
}
